import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'

import { cn } from '@/lib/cn'

/**
 * Dialog memakai elemen <dialog> bawaan peramban.
 *
 * Dengan begitu penguncian fokus, tombol Esc, dan lapisan atas ditangani
 * peramban sendiri — jauh lebih andal daripada menirunya dengan div, dan
 * gratis dari sisi aksesibilitas.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    const handleCancel = (event: Event) => {
      event.preventDefault()
      onClose()
    }
    dialog.addEventListener('cancel', handleCancel)
    return () => dialog.removeEventListener('cancel', handleCancel)
  }, [onClose])

  return (
    <dialog
      ref={ref}
      aria-labelledby="dialog-title"
      className={cn(
        'w-[calc(100vw-2rem)] rounded-2xl border border-line bg-raised p-0 text-ink shadow-[var(--shadow-lifted)]',
        // Peramban memusatkan dialog modal lewat margin:auto bawaannya,
        // tetapi preflight Tailwind menyetel margin:0 pada semua elemen dan
        // menghapusnya — panel lalu menempel di pojok kiri atas layar.
        // Kedua sumbu perlu dikembalikan, bukan hanya sumbu tegak.
        'm-auto',
        'backdrop:bg-black/60',
        'open:rise',
        size === 'sm' && 'max-w-md',
        size === 'md' && 'max-w-xl',
        size === 'lg' && 'max-w-3xl',
      )}
      onClick={(event) => {
        // Klik pada area latar menutup dialog; klik di dalam panel tidak.
        if (event.target === ref.current) onClose()
      }}
    >
      <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-5">
        <div className="min-w-0">
          <h2 id="dialog-title" className="text-base font-bold text-ink">
            {title}
          </h2>
          {description ? <p className="mt-1 text-sm leading-relaxed text-muted">{description}</p> : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Tutup"
          className="rounded-full p-2 text-subtle transition hover:bg-sunken hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="thin-scroll max-h-[65vh] overflow-y-auto px-6 py-6">{children}</div>

      {footer ? (
        <div className="flex flex-col-reverse gap-2 border-t border-line px-6 py-5 sm:flex-row sm:justify-end">
          {footer}
        </div>
      ) : null}
    </dialog>
  )
}
